/**
 * Phase 14 Plan 14-02 — ApproverRejectDialog
 *
 * Dialog for the Approver to reject a Checked entry. Requires:
 *  - reason: one of the 5 RejectionReason values (required)
 *  - remarks: min 10 chars (required) — T-14-20 client UX; server re-validates.
 *  - routeTo: radio Maker | Checker (required)
 *
 * Uses react-hook-form + Zod via useZodForm.
 */

import type { JSX } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useApproverRejectMutation } from '@/hooks/useLogsheetApproverQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import {
  REJECTION_REASON_LABELS,
  type ApproverRejectionRouteTarget,
  type RejectionReason,
} from '@/types/logsheet-approver';

const VALID_REASONS = [
  'IncorrectKpi',
  'MissingDoc',
  'WrongMachine',
  'DateMismatch',
  'Other',
] as const satisfies readonly RejectionReason[];

const VALID_ROUTE_TARGETS = [
  'Maker',
  'Checker',
] as const satisfies readonly ApproverRejectionRouteTarget[];

const rejectSchema = z.object({
  reason: z.enum(VALID_REASONS, { required_error: 'Reason is required' }),
  remarks: z.string().trim().min(10, 'Remarks must be at least 10 characters').max(500),
  routeTo: z.enum(VALID_ROUTE_TARGETS, { required_error: 'Route target is required' }),
});

export interface ApproverRejectDialogProps {
  readonly open: boolean;
  readonly entryId: number;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function ApproverRejectDialog({
  open,
  entryId,
  onClose,
  onSuccess,
}: ApproverRejectDialogProps): JSX.Element {
  const form = useZodForm({
    schema: rejectSchema,
    defaultValues: { remarks: '' },
  });

  const mutation = useApproverRejectMutation();

  function handleClose(): void {
    form.reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(
      {
        id: entryId,
        body: {
          reason: values.reason,
          remarks: values.remarks.trim(),
          routeTo: values.routeTo,
        },
      },
      {
        onSuccess: () => {
          toast.success('Entry rejected');
          form.reset();
          onSuccess();
        },
        onError: (err) => {
          toast.error('Could not reject entry', {
            description: err.detail ?? err.message,
          });
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="approver-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject Entry #{entryId}</DialogTitle>
          <DialogDescription>
            Reject this logsheet entry. Choose whether to route back to the Maker (for
            entry-correction) or back to the Checker (for re-review). Provide a clear reason and
            remarks (minimum 10 characters).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-4 py-2">
            {/* Reason */}
            <div className="space-y-1">
              <Label htmlFor="approver-reject-reason">Reason *</Label>
              <Controller
                name="reason"
                control={form.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      if (VALID_REASONS.includes(v as RejectionReason)) {
                        field.onChange(v);
                      }
                    }}
                  >
                    <SelectTrigger
                      id="approver-reject-reason"
                      data-testid="approver-reject-dialog-reason"
                    >
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      {VALID_REASONS.map((r) => (
                        <SelectItem
                          key={r}
                          value={r}
                          data-testid={`approver-reject-dialog-reason-${r}`}
                        >
                          {REJECTION_REASON_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.reason !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.reason.message}</p>
              )}
            </div>

            {/* Remarks */}
            <div className="space-y-1">
              <Label htmlFor="approver-reject-remarks">Remarks * (min 10 characters)</Label>
              <Textarea
                id="approver-reject-remarks"
                placeholder="Describe the issue in detail…"
                rows={4}
                data-testid="approver-reject-dialog-remarks"
                {...form.register('remarks')}
              />
              {form.formState.errors.remarks !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.remarks.message}</p>
              )}
            </div>

            {/* RouteTo radio */}
            <div className="space-y-2">
              <Label>Route to *</Label>
              <Controller
                name="routeTo"
                control={form.control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={(v) => {
                      if (VALID_ROUTE_TARGETS.includes(v as ApproverRejectionRouteTarget)) {
                        field.onChange(v);
                      }
                    }}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="Maker"
                        id="route-to-maker"
                        data-testid="approver-reject-dialog-route-to-Maker"
                      />
                      <Label htmlFor="route-to-maker" className="font-normal">
                        Maker — Send back for entry correction (Status → Rejected)
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="Checker"
                        id="route-to-checker"
                        data-testid="approver-reject-dialog-route-to-Checker"
                      />
                      <Label htmlFor="route-to-checker" className="font-normal">
                        Checker — Re-route for re-review (Status → Submitted, tagged &ldquo;From
                        Approver&rdquo;)
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
              {form.formState.errors.routeTo !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.routeTo.message}</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="approver-reject-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={mutation.isPending}
              data-testid="approver-reject-dialog-submit"
            >
              {mutation.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
